/**
 * Configuration multi-marque Smile World / Laser World / Vertical World
 * Centralise toutes les infos spécifiques à chaque marque
 */

export type Brand = 'smile' | 'laser' | 'vertical';

export interface BrandColors {
  background: string;      // Couleur de fond principale (header/footer emails)
  accent: string;          // Couleur d'accent (boutons, liens, highlights)
  accentDark: string;      // Version plus foncée de l'accent
  text: string;            // Couleur texte sur fond coloré
}

export interface BrandConfig {
  id: Brand;
  name: string;            // Nom affiché (ex: "Smile World")
  eventName: string;       // Nom pour les events (ex: "Smile Event")
  email: string;           // Adresse email d'envoi
  emailName: string;       // Nom affiché dans le from (ex: "Smile World")
  colors: BrandColors;
  logo: string | null;     // URL du logo (null = texte stylé)
  website: string;         // Site web principal
  cgvLinks?: Array<{ label: string; url: string }>;
  cgvUrl: string;          // URL des CGV
  directors: string[];     // Emails des directeurs par défaut
}

// Configuration Smile World
const SMILE_CONFIG: BrandConfig = {
  id: 'smile',
  name: 'Smile World',
  eventName: 'Smile Event',
  email: 'entreprises@smile-world.fr',
  emailName: 'Smile World',
  colors: {
    background: '#002B45',
    accent: '#FFC31E',
    accentDark: '#E5A800',
    text: '#FFFFFF',
  },
  logo: null, // Texte stylé "SMILE WORLD"
  website: 'www.smile-world.fr',
  cgvUrl: 'https://event.smile-world.fr/documents/cgv/smile-laser-entreprises-2026-09.pdf',
  cgvLinks: [{ label: 'CGV Entreprises', url: 'https://event.smile-world.fr/documents/cgv/smile-laser-entreprises-2026-09.pdf' }, { label: 'CGV Associations et collectivités', url: 'https://event.smile-world.fr/documents/cgv/smile-laser-associations-2026-09.pdf' }],
  directors: [], // Mapping par centre dans centerDirectors.ts
};

// Configuration Laser World
const LASER_CONFIG: BrandConfig = {
  id: 'laser',
  name: 'Laser World',
  eventName: 'Laser Event',
  email: 'devis@event.laser-world.fr',
  emailName: 'Laser World',
  colors: {
    background: '#08132C',
    accent: '#70A7F9',
    accentDark: '#4A8AD9',
    text: '#FFFFFF',
  },
  logo: 'https://cdn.prod.website-files.com/68c620cc0619e1f67108e75b/691de8e3c2f1629ef611e643_20251118%20-%20Logo%20Laser%20World.png',
  website: 'www.laser-world-paris.fr',
  cgvUrl: 'https://event.smile-world.fr/documents/cgv/smile-laser-entreprises-2026-09.pdf',
  cgvLinks: [{ label: 'CGV Entreprises', url: 'https://event.smile-world.fr/documents/cgv/smile-laser-entreprises-2026-09.pdf' }, { label: 'CGV Associations et collectivités', url: 'https://event.smile-world.fr/documents/cgv/smile-laser-associations-2026-09.pdf' }],
  directors: ['l.robert@erigea-loisirs.com'], // Même directeur pour tous les centres Laser
};

// Configuration Vertical World
const VERTICAL_CONFIG: BrandConfig = {
  id: 'vertical',
  name: 'Vertical World',
  eventName: 'Vertical Event',
  email: 'event@app.vertical-world.fr',
  emailName: 'Vertical World',
  colors: {
    background: '#724694',
    accent: '#B5DFF8',
    accentDark: '#3F61AA',
    text: '#FFFFFF',
  },
  logo: 'https://cdn.webindiz.fr/vertical-world/vertical%20world%20logo.png',
  website: 'www.vertical-world.fr',
  cgvUrl: 'https://www.vertical-world.fr/conditions-generales-de-vente',
  directors: [],
};

// Export de la config par marque
export const BRAND_CONFIG: Record<Brand, BrandConfig> = {
  smile: SMILE_CONFIG,
  laser: LASER_CONFIG,
  vertical: VERTICAL_CONFIG,
};

// Centres Laser World (pour détection automatique)
export const LASER_CENTERS = [
  'Arcueil - La Vache Noire',
  'La Défense - La Garenne',
  'Paris Montparnasse',
];

// Centres Smile World (pour référence)
export const SMILE_CENTERS = [
  'Gaité Montparnasse',
  'Lecourbe',
  'Chatillon',
  'Montigny',
  'Bercy',
  'Créteil',
  'Cergy',
  'Montmartre',
  'Cabaret',
];

// Centres Vertical World (un seul centre pour l'instant)
export const VERTICAL_CENTERS = [
  'Vertical World',
];

export function getCentersForBrand(brand: Brand | string | null | undefined): string[] {
  if (brand === 'laser') return [...LASER_CENTERS];
  if (brand === 'vertical') return [...VERTICAL_CENTERS];
  return [...SMILE_CENTERS];
}

export function mergeCenterOptions(
  ...groups: Array<ReadonlyArray<string | null | undefined>>
): string[] {
  const values = groups
    .flat()
    .filter((center): center is string => Boolean(center));

  return [...new Set(values)].sort();
}

/**
 * Détecte la marque à partir du nom du centre
 */
export function detectBrandFromCenter(centerName: string | null): Brand {
  if (!centerName) return 'smile';
  
  const normalized = centerName.toLowerCase();
  
  // Détection Laser World
  if (
    normalized.includes('laser') ||
    normalized.includes('défense') ||
    normalized.includes('defense') ||
    normalized.includes('garenne') ||
    normalized.includes('arcueil')
  ) {
    return 'laser';
  }
  
  // Détection Vertical World
  if (normalized.includes('vertical')) {
    return 'vertical';
  }
  
  // Par défaut : Smile World
  return 'smile';
}

/**
 * Détecte la marque à partir du nom du formulaire Fillout
 */
export function detectBrandFromFormName(formName: string | null): Brand {
  if (!formName) return 'smile';
  const lower = formName.toLowerCase();
  if (lower.includes('vertical')) return 'vertical';
  if (lower.includes('laser')) return 'laser';
  return 'smile';
}

/**
 * Récupère la config d'une marque
 */
export function getBrandConfig(brand: Brand | string): BrandConfig {
  if (brand in BRAND_CONFIG) return BRAND_CONFIG[brand as Brand];
  return BRAND_CONFIG.smile;
}

